import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { TestimonialsSection } from '../components/TestimonialsSection';
import { motion } from 'framer-motion';
import { ArrowRight, Camera, Zap, Eye, Compass, Radio, Settings } from 'lucide-react';

export const HomePage: React.FC = () => {
  return (
    <main className="min-h-screen bg-white text-zinc-900 overflow-x-hidden">
      <Header />
      
      {/* Hero Section with Video Background */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32 mt-16 overflow-hidden">
        {/* Background video simulation with gradient */}
        <div 
          className="absolute inset-0 bg-gradient-to-b from-blue-600 via-blue-400 to-cyan-300 opacity-80"
          style={{
            backgroundImage: `linear-gradient(135deg, #1e3c72 0%, #2a5298 25%, #7e8ba3 50%, #dbedef 75%, #f0f4f8 100%)`,
          }}
        />
        
        {/* Animated drone silhouette pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1200 600" preserveAspectRatio="none">
            <defs>
              <pattern id="drones" x="0" y="0" width="200" height="300" patternUnits="userSpaceOnUse">
                <path d="M100,150 L60,120 L60,180 L100,150 M100,150 L140,120 L140,180 Z" fill="white" opacity="0.1"/>
              </pattern>
            </defs>
            <rect width="1200" height="600" fill="url(#drones)"/>
          </svg>
        </div>

        <div className="container mx-auto max-w-5xl text-center relative z-10">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-6xl md:text-7xl font-bold text-white drop-shadow-lg leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Aerial Vision, Precision Excellence
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl text-gray-100 max-w-3xl mx-auto leading-relaxed drop-shadow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Elevating industries with professional drone services. From aerial photography to critical infrastructure inspections, we deliver unmatched precision and operational excellence.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <a
                href="/contact"
                className="flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg shadow-lg hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all duration-200"
              >
                <span className="mr-2">Free Consultation</span>
                <ArrowRight className="w-5 h-5" />
              </a>

              <a
                href="/services"
                className="flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-200"
              >
                Explore Services
              </a>
            </motion.div>

            {/* Stats */}
            <motion.div
              className="flex flex-col sm:flex-row justify-center items-center gap-8 pt-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              {[
                { number: '100+', label: 'Projects Completed' },
                { number: '100%', label: 'Client Satisfaction' },
                { number: '24/7', label: 'Support Available' },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl font-bold text-white drop-shadow">{stat.number}</div>
                  <div className="text-sm text-gray-100 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Key Services Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-4">Our Core Services</h2>
            <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
              Professional drone solutions tailored to your industry needs
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Camera,
                title: 'Aerial Photography & Videography',
                description: 'Stunning visual content from unique perspectives for marketing, real estate, and events.',
                color: 'from-blue-500 to-cyan-500'
              },
              {
                icon: Compass,
                title: 'Mapping & Surveying',
                description: 'Precise geospatial data collection for construction, agriculture, and environmental projects.',
                color: 'from-green-500 to-emerald-500'
              },
              {
                icon: Eye,
                title: 'Infrastructure Inspection',
                description: 'Safe and accurate inspection of buildings, bridges, power lines, and renewable installations.',
                color: 'from-orange-500 to-red-500'
              },
              {
                icon: Zap,
                title: 'Thermal Imaging',
                description: 'Advanced thermal analysis for diagnostics and quality assurance across industries.',
                color: 'from-purple-500 to-pink-500'
              },
              {
                icon: Radio,
                title: 'Search & Rescue Support',
                description: 'Rapid response capabilities with advanced sensors for emergency operations.',
                color: 'from-red-500 to-orange-500'
              },
              {
                icon: Settings,
                title: 'Custom Solutions',
                description: 'Bespoke drone operations designed for your specific operational requirements.',
                color: 'from-indigo-500 to-purple-500'
              },
            ].map((service, index) => (
              <motion.div
                key={service.title}
                className="p-8 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 hover:border-blue-300 transition-all duration-300 group"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className={`inline-block p-4 rounded-lg bg-gradient-to-br ${service.color} text-white mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <service.icon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold text-zinc-900 mb-2">{service.title}</h3>
                <p className="text-zinc-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="container mx-auto max-w-5xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-zinc-900 mb-4">Client Testimonials</h2>
            <p className="text-xl text-zinc-600 max-w-2xl mx-auto">
              What industry leaders say about our drone services
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Rui Patoleia',
                company: 'Pentagon Prestige LDA',
                role: 'Project Director',
                testimonial: 'Sightline UAV delivered exceptional results on our Adega project. The precision and professionalism exceeded our expectations.',
                rating: 5
              },
              {
                name: 'Rachel Goldman',
                company: 'National Power Authority',
                role: 'Infrastructure Manager',
                testimonial: 'Their thermal imaging capabilities saved us thousands in maintenance costs. Incredibly precise work.',
                rating: 5
              },
              {
                name: 'Tom Hazan',
                company: 'Kibbutz Tzivon',
                role: 'Farm Manager',
                testimonial: 'The agricultural mapping services have transformed our operations. Accurate, reliable, and truly professional.',
                rating: 5
              },
            ].map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                className="p-8 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-yellow-400">★</span>
                  ))}
                </div>
                <p className="text-zinc-700 mb-4 italic">"{testimonial.testimonial}"</p>
                <div>
                  <p className="font-semibold text-zinc-900">{testimonial.name}</p>
                  <p className="text-sm text-zinc-600">{testimonial.role}</p>
                  <p className="text-sm text-blue-600">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600 to-cyan-500">
        <div className="container mx-auto max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Elevate Your Project?</h2>
            <p className="text-xl text-gray-100 mb-8">
              Schedule a free consultation with our experts and discover how drone technology can transform your operations.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Request Your Free Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};
